"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { slugify } from "@/lib/utils";

export interface ProductionPerson {
  name: string;
  role: "yonetmen" | "senarist" | "oyuncu";
  personId?: string | null;
  characterName?: string | null;
}

export interface Production {
  slug: string;
  title: string;
  kind: string;
  year?: number | null;
  channel?: string | null;
  posterUrl?: string | null;
  status: "yayinda" | "hazirlaniyor" | "bitti";
  source?: string | null;
  people: ProductionPerson[];
}

interface Labels {
  director: string;
  writer: string;
  cast: string;
  upcoming: string;
  detail: string;
}

function PersonName({ person, locale }: { person: ProductionPerson; locale: string }) {
  if (!person.personId) {
    return <span className="text-foreground">{person.name}</span>;
  }
  return (
    <a
      href={`/${locale}/biographies/${slugify(person.name)}-${person.personId}`}
      className="font-semibold text-navy underline-offset-2 hover:underline dark:text-white"
    >
      {person.name}
    </a>
  );
}

function Names({ people, locale }: { people: ProductionPerson[]; locale: string }) {
  return (
    <span>
      {people.map((p, i) => (
        <span key={`${p.name}-${i}`}>
          {i > 0 && ", "}
          <PersonName person={p} locale={locale} />
        </span>
      ))}
    </span>
  );
}

/**
 * Afis izgarasi + kunye penceresi. Adres cubugundaki #slug ile acilir: oyuncu sayfasindaki
 * kariyer afisi /diziler#slug adresine gonderir, pencere kendiliginden acilir.
 */
export function ProductionsGrid({
  productions,
  locale,
  labels,
}: {
  productions: Production[];
  locale: string;
  labels: Labels;
}) {
  const [acik, setAcik] = useState<Production | null>(null);
  const rol = (p: Production, r: ProductionPerson["role"]) => p.people.filter((k) => k.role === r);

  useEffect(() => {
    const hashtenAc = () => {
      const slug = decodeURIComponent(window.location.hash.replace(/^#/, ""));
      if (!slug) return;
      const p = productions.find((x) => x.slug === slug);
      if (p) setAcik(p);
    };
    // Hidratlama bitmeden pencere acilirsa Radix arka plana aria-hidden basar ve React
    // sunucu/istemci uyusmazligi uyarisi verir; bu yuzden ilk acilis bir tik gecikir.
    const zamanlayici = window.setTimeout(hashtenAc, 400);
    window.addEventListener("hashchange", hashtenAc);
    return () => {
      window.clearTimeout(zamanlayici);
      window.removeEventListener("hashchange", hashtenAc);
    };
  }, [productions]);

  const kapat = () => {
    setAcik(null);
    if (window.location.hash) {
      history.replaceState(null, "", window.location.pathname + window.location.search);
    }
  };

  return (
    <>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3 md:gap-4">
        {productions.map((p) => (
          <button
            key={p.slug}
            id={p.slug}
            type="button"
            onClick={() => setAcik(p)}
            aria-label={`${p.title}: ${labels.detail}`}
            className="group/afis flex flex-col text-left"
          >
            <div className="relative aspect-[2/3] w-full overflow-hidden rounded-lg bg-black shadow-sm transition-shadow duration-300 group-hover/afis:shadow-xl">
              <Image
                src={p.posterUrl as string}
                alt={p.title}
                fill
                sizes="(max-width: 640px) 33vw, (max-width: 1024px) 20vw, 12vw"
                className="object-cover transition-transform duration-700 group-hover/afis:scale-105"
              />
              {p.status === "hazirlaniyor" && (
                <span className="absolute left-1.5 top-1.5 rounded bg-white/90 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-navy">
                  {labels.upcoming}
                </span>
              )}
            </div>
            <h3
              className="mt-2 truncate px-0.5 text-xs font-bold text-foreground group-hover/afis:text-primary md:text-sm"
              style={{ lineHeight: 1.15 }}
            >
              {p.title}
            </h3>
            {p.channel && (
              <span className="truncate px-0.5 text-[11px] text-muted-foreground">{p.channel}</span>
            )}
          </button>
        ))}
      </div>

      <Dialog open={acik !== null} onOpenChange={(o) => !o && kapat()}>
        <DialogContent className="max-h-[92vh] max-w-2xl gap-0 overflow-y-auto p-0 sm:rounded-xl">
          {acik && (
            <div className="flex flex-col sm:flex-row">
              <div className="relative h-60 w-full shrink-0 bg-black sm:aspect-[2/3] sm:h-auto sm:w-56">
                <Image
                  src={acik.posterUrl as string}
                  alt={acik.title}
                  fill
                  sizes="(max-width: 640px) 100vw, 224px"
                  className="object-cover object-top sm:object-center"
                />
              </div>
              <div className="flex-1 p-5 sm:p-6">
                <DialogTitle className="text-xl font-bold text-foreground">{acik.title}</DialogTitle>
                <p className="mt-1 text-sm text-muted-foreground">
                  {[acik.channel, acik.year, acik.status === "hazirlaniyor" ? labels.upcoming : null]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
                <dl className="mt-4 space-y-3 text-sm leading-relaxed">
                  {(["yonetmen", "senarist", "oyuncu"] as const).map((r) =>
                    rol(acik, r).length > 0 ? (
                      <div key={r}>
                        <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          {r === "yonetmen" ? labels.director : r === "senarist" ? labels.writer : labels.cast}
                        </dt>
                        <dd>
                          <Names people={rol(acik, r)} locale={locale} />
                        </dd>
                      </div>
                    ) : null
                  )}
                </dl>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
